import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Spin } from 'antd';

const TrialSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);

  // Users from /offers get campaign=true in the URL — they get a 100% discount,
  // not a 7-day trial. Skip the trial messaging and redirect them straight to the dashboard.
  const isCampaign = searchParams.get('campaign') === 'true';

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('accessToken');

    if (!sessionId || !userId || !token) {
      navigate('/login');
      return;
    }

    // Call the verify-trial endpoint
    // This endpoint must await Stripe to confirm the subscription is 'complete'
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/trial/verify?session_id=${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        if (res.data?.success) {
          // Set flag only after Stripe confirms success — so back-button guard fires correctly
          localStorage.setItem('justCompletedTrial', 'true');

          if (isCampaign) {
            // Campaign/offers users: no trial, just redirect to dashboard immediately
            navigate('/', { replace: true });
          } else {
            // Onboarding users: poll until DB reflects the new plan, then redirect
            pollForPlanUpdate(userId, token);
          }
        } else {
          navigate('/onboarding');
        }
      })
      .catch((err) => {
        console.error('Failed to verify trial:', err);
        // Fallback: assume success if we got here from Stripe, polling will verify
        if (isCampaign) {
          navigate('/', { replace: true });
        } else {
          pollForPlanUpdate(userId, token);
        }
      });

  }, [searchParams, navigate, isCampaign]);

  const pollForPlanUpdate = (userId: string, token: string, attempts = 0) => {
    if (attempts > 5) {
      // Give up polling and just send them in, they might hit a refresh race condition but it's better than hanging
      setTimeout(() => navigate('/', { replace: true }), 1000);
      return;
    }

    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/plan-details?userId=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        if (res.data?.hasSubscription === true) {
          setTimeout(() => navigate('/', { replace: true }), 1000); // 1s buffer for nice UX
        } else {
          setTimeout(() => pollForPlanUpdate(userId, token, attempts + 1), 1000);
        }
      })
      .catch(() => {
        setTimeout(() => pollForPlanUpdate(userId, token, attempts + 1), 1000);
      });
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Spin size="large" />
      {isCampaign ? (
        <>
          <h2 style={{ marginTop: '20px', color: '#333' }}>You're all set!</h2>
          <p style={{ color: '#666' }}>Taking you to your dashboard…</p>
        </>
      ) : (
        <>
          <h2 style={{ marginTop: '20px', color: '#333' }}>Your 7-day free trial has started.</h2>
          <p style={{ color: '#666' }}>Please wait while we confirm your free trial.</p>
        </>
      )}
    </div>
  );
};

export default TrialSuccess;
